import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import Button from '../../components/ui/Button';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import PipelineStats from './components/PipelineStats';
import PipelineFilters from './components/PipelineFilters';
import PipelineColumn from './components/PipelineColumn';
import AddDealModal from './components/AddDealModal';
import ComponentIdBadge from '../../components/ui/ComponentIdBadge';
import SampleDataToggle from '../../components/ui/SampleDataToggle';
import { sampleDataService } from '../../services/sampleDataService';

import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeEntity } from '../../hooks/useRealtimeEntity';

export default function Pipeline() {
  const { profile } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAddDealModalOpen, setIsAddDealModalOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSampleData, setShowSampleData] = useState(false);
  const [sampleDeals, setSampleDeals] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    assignedTo: 'all',
    dateRange: 'all',
    minValue: '',
    maxValue: ''
  });

  // Read initial state from localStorage
  const getInitialSampleDataState = () => {
    try {
      const stored = localStorage?.getItem(`crm_sample_data_enabled_${profile?.tenant_id}`);
      return stored === 'true';
    } catch {
      return false;
    }
  };

  // Pipeline stages configuration
  const pipelineStages = [
    { id: 'new', name: 'New', color: 'bg-blue-500' },
    { id: 'qualified', name: 'Qualified', color: 'bg-yellow-500' },
    { id: 'proposal', name: 'Proposal', color: 'bg-purple-500' },
    { id: 'negotiation', name: 'Negotiation', color: 'bg-orange-500' },
    { id: 'won', name: 'Won', color: 'bg-green-500' },
    { id: 'lost', name: 'Lost', color: 'bg-red-500' }
  ];

  // Real-time pipeline deals synchronization
  const transformDeal = (dbDeal) => ({
    id: dbDeal?.id,
    tenantId: dbDeal?.tenant_id,
    title: dbDeal?.title,
    value: dbDeal?.value,
    stage: dbDeal?.stage,
    probability: dbDeal?.probability,
    expectedCloseDate: dbDeal?.expected_close_date,
    contactId: dbDeal?.contact_id,
    assignedTo: dbDeal?.assigned_to,
    description: dbDeal?.description,
    tags: dbDeal?.tags,
    createdAt: dbDeal?.created_at,
    updatedAt: dbDeal?.updated_at
  });

  const { data: realtimeDeals, setData: setRealtimeDeals, isConnected } = useRealtimeEntity(
    'deals',
    deals,
    transformDeal
  );

  // Sync real-time data with local state
  useEffect(() => {
    if (isConnected) {
      setDeals(realtimeDeals);
    }
  }, [realtimeDeals, isConnected]);

  // Load sample data if toggle is already active on mount
  useEffect(() => {
    if (showSampleData && profile?.tenant_id) {
      loadSampleData();
    }
  }, [profile?.tenant_id]);

  const loadDeals = async () => {
    try {
      setLoading(true);
      // Load deals from API or use mock data
    } catch (error) {
      console.error('Error loading deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSampleData = async () => {
    try {
      const data = await sampleDataService?.getSampleDataByEntity('deals', profile?.tenant_id);
      setSampleDeals(data || []);
    } catch (err) {
      console.error('Error loading sample data:', err);
    }
  };

  useEffect(() => {
    loadDeals();
  }, [profile?.tenant_id]);

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  const handleAddDeal = (stageId = null) => {
    setSelectedStage(stageId);
    setIsAddDealModalOpen(true);
  };

  const handleSaveDeal = (newDeal) => {
    setDeals((prevDeals) => [...prevDeals, newDeal]);
  };

  const handleDealMove = (dealId, newStageId) => {
    setDeals((prevDeals) =>
      prevDeals?.map((deal) =>
        deal?.id === dealId ?
          { ...deal, stage: newStageId, updatedAt: new Date()?.toISOString() } :
          deal
      )
    );
  };

  const handleEditDeal = (deal) => {
    console.log('Edit deal:', deal);
    // Implement edit functionality
  };

  const handleDeleteDeal = (dealId) => {
    if (window.confirm('Are you sure you want to delete this deal?')) {
      setDeals((prevDeals) => prevDeals?.filter((deal) => deal?.id !== dealId));
    }
  };

  const handleCloneDeal = (deal) => {
    const clonedDeal = {
      ...deal,
      id: `deal-${Date.now()}`,
      title: `${deal?.title} (Copy)`,
      createdAt: new Date()?.toISOString(),
      updatedAt: new Date()?.toISOString()
    };
    setDeals((prevDeals) => [...prevDeals, clonedDeal]);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      assignedTo: 'all',
      dateRange: 'all',
      minValue: '',
      maxValue: ''
    });
  };

  const handleSampleDataToggle = async (isActive) => {
    try {
      setShowSampleData(isActive);
      if (isActive) {
        await loadSampleData();
      } else {
        setSampleDeals([]);
      }
    } catch (err) {
      console.error('Error toggling sample data:', err);
    }
  };

  const getFilteredDeals = () => {
    // Combine real and sample data if sample data is active
    const allDeals = showSampleData ? [...deals, ...sampleDeals] : deals;
    
    return allDeals?.filter((deal) => {
      // Search filter
      if (filters?.search && !deal?.title?.toLowerCase()?.includes(filters?.search?.toLowerCase()) &&
        !deal?.accountName?.toLowerCase()?.includes(filters?.search?.toLowerCase())) {
        return false;
      }

      // Owner filter
      if (filters?.assignedTo && filters?.assignedTo !== 'all' && deal?.assignedTo?.id !== filters?.assignedTo) {
        return false;
      }

      // Date range filter
      if (filters?.dateRange && filters?.dateRange !== 'all') {
        const closeDate = new Date(deal.closeDate);
        const today = new Date();

        switch (filters?.dateRange) {
          case 'this-week':
            const weekStart = new Date(today);
            weekStart?.setDate(today?.getDate() - today?.getDay());
            const weekEnd = new Date(weekStart);
            weekEnd?.setDate(weekStart?.getDate() + 6);
            if (closeDate < weekStart || closeDate > weekEnd) return false;
            break;
          case 'this-month':
            if (closeDate?.getMonth() !== today?.getMonth() || closeDate?.getFullYear() !== today?.getFullYear()) {
              return false;
            }
            break;
          case 'this-quarter':
            const quarter = Math.floor(today?.getMonth() / 3);
            const dealQuarter = Math.floor(closeDate?.getMonth() / 3);
            if (dealQuarter !== quarter || closeDate?.getFullYear() !== today?.getFullYear()) {
              return false;
            }
            break;
          case 'custom':
            if (filters?.startDate && closeDate < new Date(filters.startDate)) return false;
            if (filters?.endDate && closeDate > new Date(filters.endDate)) return false;
            break;
        }
      }

      return true;
    });
  };

  const filteredDeals = getFilteredDeals();

  const getDealsByStage = (stageId) => {
    return filteredDeals?.filter((deal) => deal?.stage === stageId);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <ComponentIdBadge 
        id="pg-pipeline-main" 
        name="Pipeline Page"
        description="Visual pipeline board with drag-and-drop deal management"
        position="top-right"
      />
      <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onToggleSidebar={handleSidebarToggle} />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto p-6">
            <div className="flex justify-between items-center mb-6 relative">
              <div>
                <ComponentIdBadge 
                  id="hdr-pipeline-title" 
                  name="Pipeline Page Header"
                  description="Page title and pipeline overview"
                  position="inline"
                  showLabel={true}
                />
                <h1 className="text-3xl font-bold text-gray-900">Sales Pipeline</h1>
                <p className="text-gray-600">Visualize and manage your deal flow</p>
              </div>
              <div className="flex items-center gap-3">
                <SampleDataToggle
                  entityType="pipeline deals"
                  onToggle={handleSampleDataToggle}
                  isActive={showSampleData}
                />
                <div className="relative">
                  <ComponentIdBadge 
                    id="btn-deal-quick-add" 
                    name="Quick Add Deal Button"
                    description="Opens modal to quickly create new deal"
                  />
                  <Button onClick={handleAddDeal} className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Add Deal
                  </Button>
                </div>
              </div>
            </div>

            <div className="relative mb-6">
              <ComponentIdBadge 
                id="cmp-pipeline-stats" 
                name="Pipeline Statistics Cards"
                description="Summary cards showing pipeline metrics"
              />
              <PipelineStats deals={deals} />
            </div>

            <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <ComponentIdBadge 
                id="cmp-pipeline-filters" 
                name="Pipeline Filters Component"
                description="Filter controls for pipeline view"
              />
              <PipelineFilters
                ownerFilter={filters?.assignedTo}
                setOwnerFilter={(value) => setFilters({...filters, assignedTo: value})}
                dateFilter={filters?.dateRange}
                setDateFilter={(value) => setFilters({...filters, dateRange: value})}
              />
            </div>

            <div className="relative flex gap-4 overflow-x-auto pb-4">
              <ComponentIdBadge 
                id="cmp-pipeline-columns" 
                name="Pipeline Columns Container"
                description="Drag-and-drop columns for different deal stages"
                position="top-left"
              />
              {pipelineStages?.map((stage) => (
                <div key={stage?.id} className="flex-shrink-0 w-80 h-full">
                  <PipelineColumn
                    stage={stage}
                    deals={getDealsByStage(stage?.id)}
                    onDealMove={handleDealMove}
                    onAddDeal={handleAddDeal}
                    onEditDeal={handleEditDeal}
                    onDeleteDeal={handleDeleteDeal}
                    onCloneDeal={handleCloneDeal}
                  />
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
      {isAddDealModalOpen && (
        <div className="relative">
          <ComponentIdBadge 
            id="mdl-deal-add" 
            name="Add Deal Modal"
            description="Modal dialog for creating new deals"
            position="top-right"
          />
          <AddDealModal
            isOpen={isAddDealModalOpen}
            onClose={() => setIsAddDealModalOpen(false)}
            onSave={handleSaveDeal}
            initialStage={selectedStage}
          />
        </div>
      )}
    </div>
  );
}